import { InfoPage } from '@/components/info-page';
import { EntityTable } from '@/components/table/entity-table';
import { TableErrorState } from '@/components/table/table-error-state';
import { Button } from '@/components/ui/button';
import { getAssessments } from '@/lib/api-calls';
import { useErrorToast } from '@/lib/error-handling';
import { CreateEditAssessment } from '@/routes/_layout/lessons/assessments/-components/create-edit-assessment';
import { AssessmentWithContent } from '@airreading/common/data';
import { lessonsSectionTabs } from '../-components/tabs';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { isAirAdmin } from '@airreading/common';
import { validateRouteRole } from '@/lib/routing';

export const Route = createFileRoute('/_layout/lessons/assessments/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: AssessmentsIndex,
});

const columnHelper = createColumnHelper<AssessmentWithContent>();
const columns = [
  columnHelper.accessor('name', { header: 'Name' }),
  columnHelper.accessor('contents', {
    header: 'Contents',
    cell: ({ getValue }) => {
      return (
        <ul>
          {getValue().map((content) => (
            <li key={content.id}>
              {content.name} ({content.format})
            </li>
          ))}
        </ul>
      );
    },
  }),
  columnHelper.accessor((row) => row, {
    id: 'actions',
    header: '',
    cell: ({ getValue }) => {
      return (
        <CreateEditAssessment
          from={getValue()}
          key={getValue().id}
          trigger={
            <Button variant={'outline'} size={'sm'}>
              Edit
            </Button>
          }
        />
      );
    },
  }),
];

function AssessmentsIndex() {
  const { data: assessments = [], isPending, error, refetch } = useQuery(getAssessments());

  useErrorToast(error, 'Error loading assessments. Please try again.');

  const table = useReactTable({
    data: assessments,
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      enableSorting: false,
    },
  });

  return (
    <>
      <InfoPage
        headerText="Assessments"
        headerActions={
          <>
            <CreateEditAssessment trigger={<Button>Create Assessment</Button>} />
          </>
        }
        tabs={lessonsSectionTabs()}
      >
        <EntityTable
          table={table}
          isLoading={isPending}
          hasError={!!error}
          errorState={<TableErrorState refetchAction={refetch} />}
        />
      </InfoPage>
    </>
  );
}
